// LLMErrorBanner.tsx - Error Banner for LLM Failures
import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface LLMErrorBannerProps {
  error: string | null;
  onDismiss: () => void;
  onRetryWithDifferentModel?: () => void;
  currentModel?: string;
}

const LLMErrorBanner: React.FC<LLMErrorBannerProps> = ({
  error,
  onDismiss,
  onRetryWithDifferentModel,
  currentModel
}) => {
  if (!error) return null;

  const isJSONParseError = error.includes('Failed to parse JSON response');
  const isModelOverload = error.includes('overloaded') || error.includes('rate limit');
  
  return (
    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-400" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-red-800">
            Generation Failed {currentModel && `(${currentModel})`}
          </h3>
          <div className="mt-1 text-sm text-red-700">
            {isJSONParseError && (
              <div>
                <p className="font-medium">JSON Parsing Error</p>
                <p className="text-xs mt-1">
                  The AI model returned malformed data. This usually happens when the model
                  produces content that doesn't match the expected format.
                </p>
              </div>
            )}
            {isModelOverload && (
              <div>
                <p className="font-medium">Model Overloaded</p>
                <p className="text-xs mt-1">
                  The AI service is currently experiencing high demand. Try again in a moment
                  or select a different model.
                </p>
              </div>
            )}
            {!isJSONParseError && !isModelOverload && (
              <div>
                <p className="font-medium">Generation Error</p>
                <p className="text-xs mt-1">{error}</p>
              </div>
            )}
          </div>
          
          {onRetryWithDifferentModel && (
            <div className="mt-3 flex space-x-2">
              <button
                onClick={onRetryWithDifferentModel}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Try Different Model
              </button>
              <button
                onClick={onDismiss}
                className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
        <div className="ml-auto pl-3">
          <button
            onClick={onDismiss}
            className="inline-flex rounded-md bg-red-50 p-1.5 text-red-400 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LLMErrorBanner;